/**
 * MonedaSelector
 * Selector de moneda + tipo de cambio con el mismo diseño compacto
 * que InvoiceTcRow (CxC). Usado en AddCxP, AddReceiptCxP y AddFacturaMasiva.
 *
 * Props:
 *   moneda          → 'CRC' | 'USD'
 *   onMonedaChange  → callback al cambiar moneda
 *   tipoCambio      → número (TC actual)
 *   onTCChange      → callback al editar el TC
 *   loadingTC       → boolean
 *   tcFuente        → 'BCCR' | 'MANUAL' | 'FALLBACK'
 *   monto           → número (monto original en la moneda seleccionada)
 *   tcLabel         → 'venta' | 'compra' | 'ref'  (opcional, default: 'venta'/'ref')
 *   disabled        → boolean
 */

import React from "react";

const BADGE = {
  BCCR:     { color: "#4ade80", text: "BCCR" },
  MANUAL:   { color: "#fb923c", text: "Manual" },
  FALLBACK: { color: "#f87171", text: "Sin TC" },
};

const fmtN = (n) =>
  new Intl.NumberFormat("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

export function MonedaSelector({
  moneda         = "CRC",
  onMonedaChange,
  tipoCambio     = 1,
  onTCChange,
  loadingTC      = false,
  tcFuente       = "BCCR",
  monto          = 0,
  tcLabel,
  disabled       = false,
}) {
  const badge    = BADGE[tcFuente] || BADGE.BCCR;
  const tc       = Number(tipoCambio) || 1;
  const montoNum = Number(monto) || 0;

  const label = tcLabel ?? (moneda === "USD" ? "venta" : "ref");

  return (
    <div style={{
      marginTop: 14,
      border: "1px solid var(--border-card)",
      borderRadius: 10,
      overflow: "hidden",
      background: "rgba(255,255,255,0.02)",
    }}>
      {/* ── Cabecera ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "9px 14px",
        borderBottom: "1px solid var(--border-card)",
        background: "rgba(249,115,22,0.04)",
      }}>
        <i className="fa-solid fa-coins" style={{ color: "#f97316", fontSize: "0.7rem" }} />
        <span style={{
          fontSize: "0.67rem", fontWeight: 700, color: "var(--text-muted)",
          letterSpacing: "0.6px", textTransform: "uppercase",
        }}>
          Moneda & Tipo de Cambio
        </span>
      </div>

      {/* ── Cuerpo ── */}
      <div style={{ padding: "13px 14px", display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Fila: segmentado CRC/USD + separador + TC */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

          {/* Segmentado */}
          <div style={{
            display: "flex",
            background: "rgba(0,0,0,0.25)",
            borderRadius: 8,
            padding: 3,
          }}>
            {["CRC", "USD"].map((opt) => (
              <button
                key={opt}
                type="button"
                disabled={disabled}
                onClick={() => onMonedaChange && onMonedaChange(opt)}
                style={{
                  padding: "5px 20px",
                  borderRadius: 6,
                  fontSize: "0.83rem",
                  fontWeight: moneda === opt ? 700 : 400,
                  background: moneda === opt
                    ? opt === "USD" ? "rgba(96,165,250,0.2)" : "rgba(249,115,22,0.2)"
                    : "transparent",
                  border: `1px solid ${moneda === opt
                    ? opt === "USD" ? "rgba(96,165,250,0.45)" : "rgba(249,115,22,0.45)"
                    : "transparent"}`,
                  color: moneda === opt
                    ? opt === "USD" ? "#60a5fa" : "#f97316"
                    : "var(--text-muted)",
                  cursor: disabled ? "default" : "pointer",
                  transition: "all 0.15s",
                }}
              >
                {opt}
              </button>
            ))}
          </div>

          {/* Separador vertical */}
          <div style={{ width: 1, height: 24, background: "var(--border-card)", flexShrink: 0 }} />

          {/* TC */}
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
              TC {label}:
            </span>
            {loadingTC ? (
              <i className="fa-solid fa-circle-notch fa-spin"
                 style={{ color: "#60a5fa", fontSize: "0.7rem" }} />
            ) : (
              <span style={{
                fontSize: "0.64rem", fontWeight: 700,
                background: `${badge.color}18`,
                border: `1px solid ${badge.color}45`,
                borderRadius: 4, padding: "2px 7px",
                color: badge.color, letterSpacing: "0.3px",
              }}>
                {badge.text}
              </span>
            )}
            <div style={{
              display: "flex", alignItems: "center", gap: 4,
              background: "rgba(0,0,0,0.2)",
              border: "1px solid var(--border-card)",
              borderRadius: 7, padding: "4px 10px 4px 9px",
            }}>
              <span style={{
                fontSize: "0.8rem", color: "var(--text-muted)",
                fontWeight: 600, lineHeight: 1,
              }}>₡</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                disabled={disabled}
                value={tc}
                onChange={(e) => onTCChange && onTCChange(parseFloat(e.target.value) || 1)}
                style={{
                  width: 82,
                  background: "transparent",
                  border: "none",
                  color: "var(--text-primary)",
                  fontSize: "0.88rem",
                  fontFamily: "monospace",
                  fontWeight: 600,
                  outline: "none",
                  padding: 0,
                }}
              />
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                / USD
              </span>
            </div>
          </div>
        </div>

        {/* ── Equivalencia ── */}
        {montoNum > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            background: "rgba(0,0,0,0.15)", borderRadius: 7, padding: "8px 12px",
          }}>
            <i className="fa-solid fa-arrow-right-arrow-left"
               style={{ color: "var(--text-muted)", fontSize: "0.62rem", flexShrink: 0 }} />
            {moneda === "USD" ? (
              <>
                <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ${fmtN(montoNum)}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>USD equivale a</span>
                <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ₡{fmtN(montoNum * tc)}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>al TC {tc.toFixed(2)}</span>
              </>
            ) : (
              <>
                <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ₡{fmtN(montoNum)}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>equivale a</span>
                <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ${(montoNum / tc).toFixed(2)} USD
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>al TC {tc.toFixed(2)}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MonedaSelector;
